import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, ShieldCheck, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { PolicyForm } from '@/features/policies/PolicyForm';
import { usePolicies } from '@/queries/policies';
import { useApiError } from '@/lib/useApiError';
import type { PolicyRule } from '@/schemas/admin/policies';

export const Route = createFileRoute('/_authed/policies')({
  component: PoliciesScreen,
});

type Mode = { kind: 'list' } | { kind: 'edit'; rule: PolicyRule } | { kind: 'create' };

function PoliciesScreen() {
  const { t } = useTranslation('policies');
  const policies = usePolicies();
  const resolveError = useApiError();
  const [mode, setMode] = React.useState<Mode>({ kind: 'list' });

  const errorInfo = policies.error ? resolveError(policies.error) : null;

  const columns: readonly DataColumn<PolicyRule>[] = [
    {
      id: 'slug',
      header: t('columns.slug'),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-sm font-medium">
          {r.slug}
        </span>
      ),
      sortBy: (r) => r.slug,
    },
    {
      id: 'title',
      header: t('columns.title'),
      cell: (r) => <span className="text-sm">{r.title_en}</span>,
    },
    {
      id: 'version',
      header: t('columns.version'),
      cell: (r) => (
        <Badge tone="neutral">{t('form.versionBadge', { version: r.version })}</Badge>
      ),
      sortBy: (r) => r.version,
    },
    {
      id: 'flags',
      header: t('columns.flags'),
      cell: (r) => (
        <div className="flex gap-2">
          {r.hard_rule ? (
            <Badge tone="danger">
              <ShieldCheck className="size-3" aria-hidden />
              <span>{t('columns.hard')}</span>
            </Badge>
          ) : null}
          {r.active ? <Badge tone="success">{t('columns.active')}</Badge> : null}
        </div>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('columns.actions')}</span>,
      align: 'end',
      cell: (r) => (
        <Button size="sm" variant="ghost" onClick={() => setMode({ kind: 'edit', rule: r })}>
          <Pencil className="size-4" aria-hidden />
          {t('actions.edit')}
        </Button>
      ),
    },
  ];

  if (mode.kind !== 'list') {
    return (
      <div className="space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {mode.kind === 'edit' ? t('editTitle') : t('createTitle')}
            </h1>
            <p className="text-sm text-[color:var(--color-fg-muted)]">
              {mode.kind === 'edit' ? t('editSubtitle') : t('createSubtitle')}
            </p>
          </div>
          <Button variant="ghost" onClick={() => setMode({ kind: 'list' })}>
            <X className="size-4" aria-hidden />
            {t('actions.back')}
          </Button>
        </header>
        <Card>
          <CardContent className="pt-6">
            <PolicyForm
              {...(mode.kind === 'edit' ? { rule: mode.rule } : {})}
              onSaved={() => setMode({ kind: 'list' })}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
          <p className="text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
        </div>
        <Button onClick={() => setMode({ kind: 'create' })}>
          <Plus className="size-4" aria-hidden />
          {t('actions.create')}
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('listTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable<PolicyRule>
            data={policies.data ?? []}
            columns={columns}
            rowKey={(r) => r.id}
            loading={policies.isLoading}
            error={errorInfo}
            empty={{
              title: t('emptyTitle'),
              body: t('emptyBody'),
            }}
            initialSort={{ id: 'slug', direction: 'asc' }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
